import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  signal,
  WritableSignal,
  Input,
  Output,
  EventEmitter,
  HostListener
} from '@angular/core';
import {VoiceRecordingComponent} from './components/voice-recording/voice-recording.component';
import {VcImageUploadComponent} from '../../builder-v2/components/outline-generation/components/create-company-info/components/vc-image-upload/vc-image-upload.component';
import { Subject, lastValueFrom } from 'rxjs';
import { takeUntil, filter, first, switchMap } from 'rxjs/operators';
import { CaptureService } from './services/capture.service';
import { HostingService } from '../website/hosting/hosting.service';
import { DbResponse } from '../../shared/interceptors/db-response';
import { DbModalService } from '../../modules/modal/modal';
import { WebsiteCreationStatesDialogComponent } from '../../shared/components/dialogs/website-creation-loading/website-creation-states-dialog.component';
import { BuilderSectionsInteractionService } from '../../builder-v2/builder-sections-interaction.service';
import {UserSubscription} from "../../user/user-subscription";
import {User} from "../../user/user";
import {CreateWebsiteService} from "../../services/local/create-website.service";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {BuilderV2Service} from "../../builder-v2/builder-v2.service";
import {BuilderHistoryService} from "../../builder-v2/builder-history.service";
import {BuilderStepsEnum} from "../../builder-v2/builder-tour.enum";
import {CommonBuilderDataService} from "../../builder-v2/services/common-builder-data.service";
import {TriggersService} from "../../builder-v2/services/triggers.service";
import {Website} from "../website/website";
import {ActivatedRoute, Router} from "@angular/router";
import {DialogService} from "../../../ng2-bootstrap-modal/dialog.service";
import {GoogleAnalyticsService} from "../../shared/services/google-analytics/google-analytics.service";
import {ConfirmDialogComponent} from "../../shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import {UserSubscriptionStatus} from "../../user/user-subscription-status";
import * as fromStore from '../../store';
import {Store} from "@ngrx/store";
import {Utils} from "../../shared/utils/utils";
import {TextUtils} from "../../shared/utils/text-utils";

@Component({
  selector: 'db-website-prompt',
  templateUrl: './website-prompt.component.html',
  styleUrls: ['./website-prompt.component.scss'],
  standalone: false
})
export class WebsitePromptComponent implements OnInit, OnDestroy {
  @ViewChild('descInput') descInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild(VoiceRecordingComponent) voiceRecording!: VoiceRecordingComponent;
  @ViewChild(VcImageUploadComponent) vcImageUpload!: VcImageUploadComponent;
  @Input() user: User;
  @Input() websites?: Website[];
  @Input() userSubscription: UserSubscription;
  @Input() disableControls: boolean = false;
  @Input() website?: Website;
  @Input() isRegenerate?: boolean;
  @Input() builderType?: 'wvc' | 'section_based';
  @Output() sendPrompt = new EventEmitter<{
    description: string;
    region: string;
    images?: any[];
    figmaUrl?: string;
    uniqueId?: string;
  }>();
  fileInput!: ElementRef<HTMLInputElement>;
  prompt = '';
  region: string;
  dataCenterLabel = '';
  regions: any[] = [];
  selectedRegion: string = '';
  uniqueId: string;
  public toggled: boolean = false;
  public isProcessing: boolean = false;
  public textareaFocused: boolean = false;
  public isDraggingFiles: boolean = false;
  private isMobileAutoGenerate: boolean = false;
  // Figma and file upload state
  showFigmaLink: WritableSignal<boolean> = signal(false);
  closeTooltip: WritableSignal<boolean> = signal(false);
  // Typing placeholder animation
  private placeholderTexts = [];
  private placeholderIndex = 0;
  private charIndex = 0;
  private isDeleting = false;
  private typeTimer: any;
  private blinkTimer: any;
  private basePlaceholder = '';
  private cursorOn = true;
  public animationStarted: boolean = false;
  public showFadeInAnimation: boolean = false;
  public isMobile: boolean = false;
  private readonly FADE_ANIMATION_KEY = 'website_prompt_fade_animation_shown';
  private readonly TYPE_MS = 30;
  private readonly CURSOR_BLINK_MS = 500;
  private destroy$ = new Subject<void>();
  private initializationPromise: Promise<void>;
  public vibeLimitations: any; // VibeCodingLimitationsData interface doesn't exist in dashboard-reseller
  private userUpgradableSubscription: any;
  private availableHostingSubscriptions: any[] = [];
  private generatedWebsitesCount: number = 0;
  public maxCharacterLimit: number = 2000;
  public showCharacterLimitError: boolean = false;
  public demoWebsite: Website;
  public companyName: String;
  private imagesFromUrl: any[] = [];

  constructor(
    private captureService: CaptureService,
    private hostingService: HostingService,
    private modalService: DbModalService,
    private builderInteractionsService: BuilderSectionsInteractionService,
    private builderV2Service: BuilderV2Service,
    private builderHistoryService: BuilderHistoryService,
    private commonDataService: CommonBuilderDataService,
    private createWebsiteService: CreateWebsiteService,
    private _route: ActivatedRoute,
    private triggersService: TriggersService,
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService,
    private _router: Router,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) {}

  ngOnInit(): void {
    this.demoWebsite = this.websites && this.websites.find((website: Website) => website.isAiBuilderDemo);
    !this.builderType && (this.builderType = this.userSubscription?.availableBuilder as any);
    const queryParams: any = (<any>this._route.queryParams).value;
    const generateWebsite = queryParams['generate-website'];
    const builderType = queryParams['builderType'];
    this.isMobile = window.innerWidth <= 991;

    // Parse prefilledAiData if available
    let description = queryParams['description'];
    if (queryParams['prefilledAiData']) {
      try {
        const prefilledData = JSON.parse(decodeURIComponent(queryParams['prefilledAiData']));
        if (prefilledData.initial_description) {
          description = prefilledData.initial_description;
        }
        // Extract images from prefilledAiData if available
        if (prefilledData.images && Array.isArray(prefilledData.images) && prefilledData.images.length > 0) {
          this.imagesFromUrl = prefilledData.images;
        }
      } catch (error) {
        console.error('Failed to parse prefilledAiData:', error);
      }
    }

    if (description) {
      this.prompt = description;
    }
    if (builderType) {
      this.builderType = builderType;
    }

    // For mobile flow with generate-website, override builderType to section_based todo
    if (Utils.isDemoUser(this.userSubscription, this.user)) {
      this.builderType = 'section_based';
      this.isMobileAutoGenerate = this.isMobile;

      // Auto-open dialog for mobile generation flow
      if (this.prompt) {
        setTimeout(() => {
          this.onSend();
        }, 100);
      }
    }

    // Check if fade-in animation should be shown (first time only)
/*    const fadeAnimationShown = localStorage.getItem(this.FADE_ANIMATION_KEY);
    if (!fadeAnimationShown) {
      this.showFadeInAnimation = true;
      localStorage.setItem(this.FADE_ANIMATION_KEY, 'true');
    }*/
    this._initializeData(generateWebsite);

    // Subscribe to vibe limitations - NOT AVAILABLE IN DASHBOARD-RESELLER
    // Vibe limitations selectors don't exist in dashboard-reseller store
    // this._store.select(fromStore.getVibeLimitationsLoaded)
    //   .pipe(
    //     filter((loaded: boolean) => loaded),
    //     switchMap(() => this._store.select(fromStore.getVibeLimitationsData)),
    //     first(),
    //     takeUntil(this.destroy$)
    //   )
    //   .subscribe((vibeLimitations: any) => {
    //     this.vibeLimitations = vibeLimitations;
    //   });

    // Subscribe to upgrade data
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserUpgradableSubscription())),
        takeUntil(this.destroy$)
      )
      .subscribe((userUpgradableSubscription) => {
        this.userUpgradableSubscription = userUpgradableSubscription;
      });
    this._store.select(fromStore.getCompanyInfoLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getCompanyInfo)),
        first()
      )
      .subscribe((companyInfo: any) => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
        this.placeholderTexts.push(TextUtils.getText('text_askAiToBuildAWebsiteThat', { name: this.companyName}));
      });
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserAvailableHostingSubscriptions())),
        takeUntil(this.destroy$)
      )
      .subscribe((availableHostingSubscriptions) => {
        this.availableHostingSubscriptions = availableHostingSubscriptions;
      });

    // Subscribe to generated websites count for section_based builder
    this._store.select(fromStore.getGeneratedWebsitesCount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((count: number) => {
        this.generatedWebsitesCount = count || 0;
      });

    // Start typing placeholder animation only the first time
    if (!this.animationStarted) {
      this.startPlaceholderAnimation();
    } else {
      // Show static placeholder without animation
      if (this.descInput?.nativeElement) {
        this.descInput.nativeElement.placeholder = this.placeholderTexts[0];
      }
    }

    // Resume cursor animation when user leaves empty textarea
    setTimeout(() => {
      if (this.descInput?.nativeElement) {
        this.descInput.nativeElement.addEventListener('blur', () => {
          if (this.prompt.length === 0) {
            this.cursorOn = true;
            this.renderPlaceholder();
          }
        });
      }
    }, 100);
  }

  private _initializeData(generateWebsite: boolean): void {
    const promises: Promise<any>[] = [];
    const regionPromise = lastValueFrom(
      this.hostingService.regions(this.userSubscription?.id)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          takeUntil(this.destroy$)
        )
    ).then((response: DbResponse) => {
      if (response.success && response.data) {
        const regionsFormatted = [];
        Object.keys(response.data).forEach((key: string): void => {
          regionsFormatted.push({
            label: response.data[key].location,
            value: response.data[key].zone,
            isAvailable: response.data[key].is_available
          });
        });
        this.regions = regionsFormatted;
        const availableRegion = this.regions.find(region => region.isAvailable);
        if (availableRegion) {
          this.selectedRegion = availableRegion.value;
          this.dataCenterLabel = availableRegion.label;
        }
      }
    });
    promises.push(regionPromise);
    const uniqueIdPromise = new Promise<void>((resolve) => {
      this.builderV2Service.getBuilderUniqueId()
        .subscribe({
          next: (response) => {
            if (response && response.data) {
              this.builderHistoryService.setSessionId(response.data.uniqueId);
              this.uniqueId = this.builderHistoryService.getUniqueId();
              this.commonDataService.uniqueId = this.uniqueId;
            }
            resolve();
          },
          error: (err) => {
            console.error('Failed to get unique ID:', err);
            resolve();
          }
        });
    });
    promises.push(uniqueIdPromise);
    this.initializationPromise = Promise.all(promises)
      .then(() => {
        if (generateWebsite && this.prompt) {
          this.onSend();
        }
      });
  }


  @HostListener('window:dragover', ['$event'])
  onWindowDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Disable drag and drop for section_based/classic builder
    if (this.builderType === 'section_based') {
      return;
    }
    if (event.dataTransfer) {
      this.isDraggingFiles = true;
    }
  }

  @HostListener('window:dragleave', ['$event'])
  onWindowDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Disable drag and drop for section_based/classic builder
    if (this.builderType === 'section_based') {
      return;
    }
    // Only hide overlay when leaving the window (relatedTarget is null)
    if (!event.relatedTarget) {
      this.isDraggingFiles = false;
    }
  }

  @HostListener('window:drop', ['$event'])
  onWindowDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFiles = false;

    // Disable drag and drop for section_based/classic builder
    if (this.builderType === 'section_based') {
      return;
    }

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0 && this.vcImageUpload) {
      // Create a synthetic event to pass to the image upload component
      const syntheticEvent = {
        target: { files: event.dataTransfer.files }
      } as any;
      this.vcImageUpload.handleFileChange(syntheticEvent);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up placeholder animation timers
    if (this.typeTimer) {
      clearTimeout(this.typeTimer);
    }
    if (this.blinkTimer) {
      clearInterval(this.blinkTimer);
    }
  }

  // ==================== Typing Placeholder Animation ====================
  private startPlaceholderAnimation(): void {
    this.animationStarted = true;
    // Start cursor blinking
    this.blinkTimer = setInterval(() => {
      if (!this.isUserInteracting()) {
        this.cursorOn = !this.cursorOn;
        this.renderPlaceholder();
      }
    }, this.CURSOR_BLINK_MS);

    // Start typing animation
    this.typePlaceholder();
  }

  private isUserInteracting(): boolean {
    // Pause animation if focused or user typed anything
    return document.activeElement === this.descInput?.nativeElement || this.prompt.length > 0;
  }

  private renderPlaceholder(): void {
    if (!this.descInput?.nativeElement) return;
    // Show cursor only when we're animating (not interacting)
    this.descInput.nativeElement.placeholder = this.basePlaceholder;
  }

  private typePlaceholder(): void {
    if (this.isUserInteracting()) {
      this.basePlaceholder = '';
      this.cursorOn = false;
      this.renderPlaceholder();
      this.typeTimer = setTimeout(() => this.typePlaceholder(), 200);
      return;
    }
    const fullText = this.placeholderTexts[this.placeholderIndex];
    this.basePlaceholder = fullText.slice(0, this.charIndex);
    this.renderPlaceholder();
    if (!this.isDeleting) {
      if (this.charIndex < fullText.length) {
        this.charIndex++;
        this.typeTimer = setTimeout(() => this.typePlaceholder(), this.TYPE_MS);
      } else {
        // Animation complete - stop and show full text without cursor
        this.cursorOn = false;
        this.basePlaceholder = fullText;
        this.renderPlaceholder();
        // Clear timers to stop animation
        if (this.typeTimer) {
          clearTimeout(this.typeTimer);
        }
        if (this.blinkTimer) {
          clearInterval(this.blinkTimer);
        }
      }
    }
  }

  // ==================== Model Selection ====================
  selectModel(model: 'wvc' | 'section_based'): void {
    // Check if switching to classic builder with attached files
    if (model === 'section_based') {
      const hasAttachedImages = this.builderInteractionsService.vibeCodingImages?.length > 0;
      if (hasAttachedImages || this.showFigmaLink()) {
        // Clear attached files from service
        this.builderInteractionsService.vibeCodingImages = [];
        this.builderInteractionsService.vibeCodingFigma = null;
        this.showFigmaLink.set(false);
        // Clear file input element
        if (this.fileInput?.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        // Clear uploaded images in VcImageUploadComponent
        if (this.vcImageUpload) {
          this.vcImageUpload.uploadedImages.set([]);
          this.vcImageUpload.hasSrc = false;
          this.vcImageUpload.figmaResp = null;
        }
        // Show notification
        NotifierComponent.notify(
          new Notification(
            'File attachments aren\'t supported in the AI Classic Builder.',
            NotificationType.DANGER
          )
        );
      }
    }
    this.builderType = model;
  }

  // ==================== Data Center Selection ====================
  onRegionChange(value: string): void {
    const region = this.regions.find(r => r.value === value);
    if (region) {
      this.selectedRegion = value;
      this.dataCenterLabel = region.label;
    }
  }

  // ==================== Voice Recording ====================
  onTranscriptUpdate(transcript: string): void {
    this.prompt += (this.prompt ? ' ' : '') + transcript;
  }

  // ==================== Add Menu Actions ====================
  onImportFromFigma(): void {
    this.showFigmaLink.set(true);
    this.closeTooltip.set(true);
    if (!this.isMobile) {
      setTimeout(() => {
        this.closeTooltip.set(false);
      }, 200);
    }
  }

  async onTakeScreenshot(): Promise<void> {
    this.closeTooltip.set(true);
    if (!this.isMobile) {
      setTimeout(() => {
        this.closeTooltip.set(false);
      }, 200);
    }
    try {
      if (!this.fileInput) {
        return;
      }
      const file = await this.captureService.onScreenshotCapture();
      if (file) {
        const fileInputElement = this.fileInput.nativeElement;
        // Clear the input first to ensure change event fires
        fileInputElement.value = '';
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputElement.files = dataTransfer.files;
        // Dispatch the change event
        const event = new Event('change', { bubbles: true });
        fileInputElement.dispatchEvent(event);
      }
    } catch (error) {
      alert('Failed to capture screenshot. Please try again.');
    }
  }

  onAttachMedia(): void {
    this.closeTooltip.set(true);
    if (!this.isMobile) {
      setTimeout(() => {
        this.closeTooltip.set(false);
      }, 200);
    }
    debugger;
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  // Handler for closing Figma input
  onCloseFigma(): void {
    this.showFigmaLink.set(false);
    this.builderInteractionsService.vibeCodingFigma = null;
  }

  // Public method to reset processing state (called by parent component)
  public resetProcessingState(): void {
    setTimeout(() => this.isProcessing = false);
  }

  // ==================== Send Message ====================

  onSend() {
    if (!this.prompt.trim() || !!this.disableControls || this.isProcessing) {
      return;
    }

    // Check character limit
    if (this.prompt.length > this.maxCharacterLimit) {
      this.showCharacterLimitError = true;
      return;
    }

    // Check limit based on builder type
    let limitReached = false;

    if (this.builderType === 'section_based') {
      // Section-based builder: check ai_builder regeneration limits
      if (this.userSubscription?.plan?.services?.ai_builder) {
        const regenerationWebsitesCount = parseInt(this.userSubscription.plan.services.ai_builder.regeneration_count?.parameter_value || '0', 10);
        const regenerationAdditionalWebsitesCount = parseInt(
          this.userSubscription.plan.additionalDomainCount &&
          this.userSubscription.plan.services.ai_builder.regeneration_additional_count ?
            this.userSubscription.plan.services.ai_builder.regeneration_additional_count.parameter_value : '0', 10)
          * (this.userSubscription.plan.additionalDomainCount || 0);
        const totalWebsitesCount = regenerationWebsitesCount + regenerationAdditionalWebsitesCount;

        limitReached = this.generatedWebsitesCount >= totalWebsitesCount;
      }
    }
    else {
      // Vibe coding: check vibe limitations
      limitReached = this.vibeLimitations?.create_limitations
        && this.vibeLimitations?.create_limitations?.already_used >= this.vibeLimitations?.create_limitations?.limitation + (this.vibeLimitations?.additional_credits?.total_credits || 0);
    }

    if (limitReached || (Utils.isDemoUser(this.userSubscription, this.user) && this.demoWebsite)) {
      return this.showExceededRegenerateDialog();
    }

    const data: any = {
      description: this.prompt,
      region: this.selectedRegion || ''
    };

    if (!this.website) {
      const canCreate = this.userSubscription?.domainsPermission?.canCreateWebsite;
      if (!canCreate && !this.website) {
        setTimeout(() => this.isProcessing = false);
        this.createWebsiteService.openLimitExceededModal(false);
        return;
      }
    }
    if (this.builderType === 'wvc') {
      this._wvcFlow(data, true);
      this._clearData();
    }
    else {
      // Wait for initialization before sending
      this._sectionBasedFlow(data, true);
    }
  }

  private _sectionBasedFlow(data, innerPage: boolean) {
    this.builderInteractionsService.vibeCodingImages = [];
    this.builderInteractionsService.vibeCodingFigma = null;
    // Keep isProcessing true - parent component will handle the flow

    // Hide prefilled animations (wave-animation and "Building" text) when coming from website prompt
    this.builderInteractionsService.hidePrefilledAnimations.next(true);

    this.isProcessing = true;

    // Let builder-v2 handle generateAiBuilderDesc like it did in the past
    if (!!this.website && !this.isMobile) {
      this.sendPrompt.emit(data);
      this._clearData();
    }
    else {
      this._openWebsiteCreationStatesDialog();
      this._clearData();
    }
  }

  private _openWebsiteCreationStatesDialog(): void {
    const width = 1320;
    const height = 760;

    // Reset only animation-related state before opening dialog
    // Don't reset builderInteractionsService as it breaks siteInfo flow
    this.triggersService.resetTriggers();
    this.builderInteractionsService.resetSubjects();

    // Prepare dialog for fresh open - this will trigger loading states and canvas zoom reset
    this.builderInteractionsService.prepareDialogForFreshOpen.next(true);

    // Set builder step to SHOW_CANVAS (builder-v2 will override to SHOW_FINALIZE for mobile auto-generate)
    this.builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);

    // Open modal immediately (don't wait for API response)
    const modal = this.modalService.open(WebsiteCreationStatesDialogComponent, { width, height, hasCloseBtn: true });
    modal.componentInstance.user = this.user;
    modal.componentInstance.companyName = this.companyName;
    modal.componentInstance.userSubscription = this.userSubscription;
    // Pass the description for both builders
    modal.componentInstance.description = this.prompt;
    modal.componentInstance.region = this.selectedRegion;
    modal.componentInstance.uniqueId = this.uniqueId;
    modal.componentInstance.step = 'outline';
    modal.componentInstance.website = this.website || null;
    modal.componentInstance.builderType = 'section_based';
    modal.componentInstance.hasCloseBtn = true;
    modal.componentInstance.isMobileAutoGenerate = this.isMobileAutoGenerate;

    const imagesFromService = this.builderInteractionsService.vibeCodingImages;
    const imagesFromUrlQuery = this.imagesFromUrl;
    const images = imagesFromUrlQuery.length > 0 ? imagesFromUrlQuery : imagesFromService;

    // Pass images if available
    if (images && images.length > 0) {
       modal.componentInstance.images = images;
    }

    modal.closed.subscribe(() => {
      console.log('Website creation dialog closed');
      setTimeout(() => this.isProcessing = false);
    });

    modal.dismissed.subscribe(() => {
      console.log('Website creation dialog dismissed');
      setTimeout(() => this.isProcessing = false);
    });
  }

  private _wvcFlow(data, innerPage: boolean) {
      // Get images from URL or from the service
      const imagesFromService = this.builderInteractionsService.vibeCodingImages;
      const imagesFromUrlQuery = this.imagesFromUrl;
      const images = imagesFromUrlQuery.length > 0 ? imagesFromUrlQuery : imagesFromService;

      // Get Figma data from the service
      const figma = this.builderInteractionsService.vibeCodingFigma;
    if (images && images.length > 0) {
      data.images = images;
    }
    if (figma && figma.screenshot_url) {
      data.figmaUrl = figma.screenshot_url;
    }
    if (this.uniqueId) {
      data.uniqueId = this.uniqueId;
    }
    if (innerPage) {
      this.sendPrompt.emit(data);
    }
    const width = 500;
    const height = 228;

    const modal = this.modalService.open(WebsiteCreationStatesDialogComponent, { width, height });
    modal.componentInstance.user = this.user;
    modal.componentInstance.companyName = this.companyName;
    // Pass the description for both builders
    modal.componentInstance.description = this.prompt;
    modal.componentInstance.region = this.selectedRegion;
    modal.componentInstance.uniqueId = this.uniqueId;
    modal.componentInstance.website = this.website || null;
    modal.componentInstance.step = 'install-package';
    modal.componentInstance.builderType = 'wvc';

    // Pass images and Figma data if available (only for vibe builder)
    if (images && images.length > 0) {
      modal.componentInstance.images = images;
    }
    if (figma && figma.screenshot_url) {
      modal.componentInstance.figmaUrl = figma.screenshot_url;
    }

    modal.closed.subscribe(() => {
      console.log('Website creation dialog closed');
      setTimeout(() => this.isProcessing = false);
    });

    modal.dismissed.subscribe(() => {
      console.log('Website creation dialog dismissed');
      setTimeout(() => this.isProcessing = false);
    });
  }

  private _performSendListing(data) {
    this.isProcessing = true;
    if (this.builderType === 'section_based') {
      // Set processing state
      this._sectionBasedFlow(data, false);
      return;
    }
    else {
      this._wvcFlow(data, false);
    }
  }

  private _clearData() {
    // Clear state after opening dialog
    this.prompt = '';
    this.builderInteractionsService.vibeCodingImages = [];
    this.builderInteractionsService.vibeCodingFigma = null;
  }

  // ==================== Toolbar Click Handler ====================
  onToolbarClick(event: MouseEvent): void {
    // Don't focus if clicking on buttons or interactive elements
    const target = event.target as HTMLElement;
    const isButton = target.tagName === 'BUTTON' || target.closest('button');
    const isInput = target.tagName === 'INPUT' || target.closest('input');

    if (!isButton && !isInput && this.descInput?.nativeElement) {
      this.descInput.nativeElement.focus();
    }
  }

  // ==================== Textarea Keyboard Handler ====================
  onTextareaKeydown(event: KeyboardEvent): void {
    // Send on Enter (but allow Shift+Enter for new lines)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    } else if (event.key === 'Enter' && event.shiftKey) {
      // Scroll to bottom after new line is added
      setTimeout(() => {
        if (this.descInput?.nativeElement) {
          this.descInput.nativeElement.scrollTop = this.descInput.nativeElement.scrollHeight;
        }
      }, 0);
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('actions-dropdown')
      && !target.parentElement?.classList.contains('actions-dropdown')) {
      this.toggled = false;
    }
  }

  public showExceededRegenerateDialog(): void {
    // Use the same logic as SubscriptionUpgradeAvailableGuard
    const hasUpgrade = !!this.userUpgradableSubscription && !((this.availableHostingSubscriptions.length === 1 && !this.userSubscription?.isApiResellerPlan) && this.availableHostingSubscriptions.filter((_userSubscription => _userSubscription?.tabCategory === this.userSubscription?.tabCategory)));

    this._dialogService
      .addDialog(ConfirmDialogComponent, {
        header: TextUtils.getText('text_youExceededTheLimitForAiGenerations'),
        message: hasUpgrade ? TextUtils.getText('text_upgradeToHigherPlanToGetMoreAiGenerations') : TextUtils.getText('text_youExceededTheLimitForAiGenerationsDesc'),
        confirmButtonText: hasUpgrade ? TextUtils.getText('text_upgrade') : TextUtils.getText('text_contactUsTitle'),
        confirmButtonClass: 'ds-btn-blue ds-btn-s m rectangle',
        cancelButtonText: TextUtils.getText('text_cancel'),
        cancelButtonClass: 'ds-btn-white-gray-border white-bg ds-btn-s m short',
        alignLeft: true,
        fromStaging: true,
        hasCloseButton: false,
        width: '700px',
        height: '186px'
      })
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          if (!hasUpgrade) {
            this.openChat();
          } else if (this.userSubscription && this.userSubscription
            .status === UserSubscriptionStatus.canceled) {
            this._router.navigate(['/workspace/subscription-plan']);
          } else {
            const analyticsData = {
              eventCategory: 'Free trial paywalls',
              eventAction: 'Upgrade button click',
              eventLabel: 'AI generation limit reached'
            };
            this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
            this._router.navigate(['/upgrade-plan']);
          }
        }
      });
  }

  private openChat() {
    window['Smooch']?.open();
    this._store.dispatch(new fromStore.ToggleChatOverlay(false));
  }
}
